import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { SupportContact } from './SupportContact';
import {
  getServiceStatus,
  sendServiceRequest,
  sendSupportMessage,
} from '../../services/supportApi';

const STOP_REASONS = [
  { id: 'cost', label: 'Cost / pricing' },
  { id: 'engagement', label: 'Not enough customer engagement' },
  { id: 'device', label: 'Device or technical issues' },
  { id: 'closing', label: 'Store closing or relocating' },
  { id: 'other', label: 'Other' },
];

const FUNNEL_STEPS = ['Feedback', 'Talk to us', 'Review', 'Confirm'];

/**
 * Service panel on the Profile page. "Manage service" opens an options menu;
 * choosing "Stop service" leads into a multi-stage retention funnel: reason →
 * customer-success offer → consequences (incl. the $50 reactivation fee per
 * the Terms) → final acknowledgement.
 */
export function ServiceControl() {
  const { user } = useAuth();
  const [status, setStatus] = useState(''); // '' until loaded, then active | stopped
  const [loading, setLoading] = useState(true);

  // UI state: menu closed → options menu → funnel stages 1..4
  const [menuOpen, setMenuOpen] = useState(false);
  const [stage, setStage] = useState(0);
  const [reasonCategory, setReasonCategory] = useState('');
  const [reason, setReason] = useState('');
  const [feeAcknowledged, setFeeAcknowledged] = useState(false);

  const [sending, setSending] = useState(false);
  const [sentMessage, setSentMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const accessToken = localStorage.getItem('accessToken');
        const data = await getServiceStatus(accessToken);
        if (!cancelled) setStatus(data.status === 'stopped' ? 'stopped' : 'active');
      } catch (err) {
        if (!cancelled) setStatus('active');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const resetFunnel = () => {
    setMenuOpen(false);
    setStage(0);
    setReasonCategory('');
    setReason('');
    setFeeAcknowledged(false);
    setError('');
  };

  const backToMenu = () => {
    setStage(0);
    setReasonCategory('');
    setReason('');
    setFeeAcknowledged(false);
    setError('');
    setMenuOpen(true);
  };

  const reasonLabel =
    STOP_REASONS.find((r) => r.id === reasonCategory)?.label || 'Not specified';

  const handleTalkToCustomerSuccess = async () => {
    setSending(true);
    setError('');
    try {
      const accessToken = localStorage.getItem('accessToken');
      await sendSupportMessage({
        accessToken: accessToken || undefined,
        email: user?.attributes?.email || '',
        topic: 'Customer success',
        message: [
          'A provider considering stopping service would like to talk to customer success.',
          `Reason: ${reasonLabel}`,
          reason ? `Details: ${reason}` : null,
        ]
          .filter(Boolean)
          .join('\n'),
      });
      resetFunnel();
      setSentMessage(
        "Thank you — our customer success team will reach out within 1 business day to see how we can make this right. Your service stays active in the meantime."
      );
    } catch (err) {
      setError(err.message || 'Could not reach customer success. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleServiceRequest = async (action) => {
    setSending(true);
    setError('');
    setSentMessage('');
    try {
      const accessToken = localStorage.getItem('accessToken');
      const data = await sendServiceRequest({
        accessToken,
        action,
        reason: action === 'stop' ? reason : '',
        reasonCategory: action === 'stop' ? reasonCategory : '',
      });
      setStatus(data.status);
      setSentMessage(data.message);
      resetFunnel();
    } catch (err) {
      setError(err.message || 'Could not send your request.');
    } finally {
      setSending(false);
    }
  };

  const renderStepIndicator = () => (
    <ol className="provider-funnel-steps" aria-label="Stop service steps">
      {FUNNEL_STEPS.map((label, index) => {
        const stepNumber = index + 1;
        const state =
          stepNumber < stage ? 'done' : stepNumber === stage ? 'current' : 'todo';
        return (
          <li key={label} className={`provider-funnel-step provider-funnel-step--${state}`}>
            <span className="provider-funnel-step-dot" aria-hidden="true">
              {stepNumber < stage ? '✓' : stepNumber}
            </span>
            <span className="provider-funnel-step-label">{label}</span>
          </li>
        );
      })}
    </ol>
  );

  return (
    <div className="provider-panel">
      <div className="provider-service-head">
        <h2 className="provider-panel-title" style={{ marginBottom: 0 }}>
          Service
        </h2>
        {!loading && status && (
          <span
            className={`provider-badge ${
              status === 'active' ? 'provider-badge--completed' : 'provider-badge--failed'
            }`}
          >
            {status === 'active' ? 'Active' : 'Stopped'}
          </span>
        )}
      </div>

      {loading && <p className="provider-service-note">Checking service status…</p>}

      {!loading && (
        <>
          {sentMessage && (
            <div className="provider-alert provider-alert--success">{sentMessage}</div>
          )}
          {error && <div className="provider-alert provider-alert--error">{error}</div>}

          {/* ---------- Active, closed ---------- */}
          {status === 'active' && stage === 0 && !menuOpen && (
            <>
              <p className="provider-service-note">
                Your CupFate device is active and generating fortunes. Should you wish to review
                your options — including pausing service — our team is here to help.
              </p>
              <button
                type="button"
                className="provider-service-stop-link"
                onClick={() => {
                  setSentMessage('');
                  setMenuOpen(true);
                }}
              >
                Manage service
              </button>
            </>
          )}

          {/* ---------- Active, options menu ---------- */}
          {status === 'active' && stage === 0 && menuOpen && (
            <div className="provider-service-menu">
              <h3 className="provider-funnel-title">Service options</h3>
              <p className="provider-service-note">
                Choose how you&apos;d like to manage your CupFate service.
              </p>

              <div className="provider-service-options">
                <div className="provider-service-option">
                  <div>
                    <p className="provider-service-option-title">Talk to our team</p>
                    <p className="provider-service-option-desc">
                      Questions, device help, or ideas to get more from your service.
                    </p>
                  </div>
                  <SupportContact
                    email={user?.attributes?.email || ''}
                    storeName={user?.attributes?.['custom:store_name'] || ''}
                    storeCode={user?.attributes?.['custom:device_id'] || ''}
                    triggerLabel="Contact support"
                    triggerClassName="provider-btn provider-btn--secondary provider-btn--sm provider-btn--inline"
                  />
                </div>

                <div className="provider-service-option">
                  <div>
                    <p className="provider-service-option-title">Stop service</p>
                    <p className="provider-service-option-desc">
                      Deactivates your device. A $50 reactivation fee applies to resume, per the{' '}
                      <Link to="/provider/terms" className="provider-link">
                        Terms of Service
                      </Link>
                      .
                    </p>
                  </div>
                  <button
                    type="button"
                    className="provider-btn provider-btn--ghost provider-btn--sm provider-btn--inline provider-btn--danger-text"
                    onClick={() => setStage(1)}
                  >
                    Stop service…
                  </button>
                </div>
              </div>

              <button
                type="button"
                className="provider-funnel-continue-link"
                onClick={resetFunnel}
              >
                Close
              </button>
            </div>
          )}

          {/* ---------- Stage 1: reason ---------- */}
          {status === 'active' && stage === 1 && (
            <div className="provider-funnel">
              {renderStepIndicator()}
              <h3 className="provider-funnel-title">Help us understand your experience</h3>
              <p className="provider-service-note">
                If you&apos;re considering pausing your service, please share what&apos;s prompting
                it — your feedback directly shapes how we serve you.
              </p>
              <div className="provider-funnel-reasons" role="radiogroup" aria-label="Reason for stopping">
                {STOP_REASONS.map((r) => (
                  <label
                    key={r.id}
                    className={`provider-funnel-reason${
                      reasonCategory === r.id ? ' provider-funnel-reason--selected' : ''
                    }`}
                  >
                    <input
                      type="radio"
                      name="stopReason"
                      value={r.id}
                      checked={reasonCategory === r.id}
                      onChange={() => setReasonCategory(r.id)}
                    />
                    <span>{r.label}</span>
                  </label>
                ))}
              </div>
              <div className="provider-form-group" style={{ marginBottom: 16 }}>
                <label className="provider-label" htmlFor="serviceStopDetails">
                  Anything else we should know? <span className="provider-label-hint">— optional</span>
                </label>
                <textarea
                  id="serviceStopDetails"
                  className="provider-input"
                  rows={3}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Tell us more about what's not working."
                />
              </div>
              <div className="provider-payment-actions">
                <button
                  type="button"
                  className="provider-btn provider-btn--secondary provider-btn--sm provider-btn--inline"
                  onClick={backToMenu}
                >
                  Back
                </button>
                <button
                  type="button"
                  className="provider-btn provider-btn--primary provider-btn--sm provider-btn--inline"
                  onClick={() => setStage(2)}
                  disabled={!reasonCategory}
                >
                  Continue
                </button>
              </div>
            </div>
          )}

          {/* ---------- Stage 2: customer success offer ---------- */}
          {status === 'active' && stage === 2 && (
            <div className="provider-funnel">
              {renderStepIndicator()}
              <h3 className="provider-funnel-title">Let us make this right first</h3>
              <p className="provider-service-note">
                Most concerns — pricing, engagement, device issues — can be resolved with a quick
                conversation. Our customer success team can review your setup, share what works
                for similar stores, and discuss options tailored to you. No commitment, and your
                service stays exactly as it is.
              </p>
              <div className="provider-payment-actions">
                <button
                  type="button"
                  className="provider-btn provider-btn--primary provider-btn--sm provider-btn--inline"
                  onClick={handleTalkToCustomerSuccess}
                  disabled={sending}
                >
                  {sending ? 'Sending…' : 'Talk to customer success'}
                </button>
                <button
                  type="button"
                  className="provider-btn provider-btn--secondary provider-btn--sm provider-btn--inline"
                  onClick={resetFunnel}
                  disabled={sending}
                >
                  Keep my service
                </button>
              </div>
              <button
                type="button"
                className="provider-funnel-continue-link"
                onClick={() => setStage(3)}
                disabled={sending}
              >
                No thanks, continue to stop
              </button>
            </div>
          )}

          {/* ---------- Stage 3: consequences ---------- */}
          {status === 'active' && stage === 3 && (
            <div className="provider-funnel">
              {renderStepIndicator()}
              <h3 className="provider-funnel-title">Please review before continuing</h3>
              <ul className="provider-funnel-consequences">
                <li>
                  Your device is <strong>deactivated immediately</strong> and stops generating
                  fortunes.
                </li>
                <li>
                  Usage accrued before the stop <strong>remains billable</strong> on your next
                  invoice.
                </li>
                <li>
                  Resuming service requires a <strong>$50 reactivation fee</strong> per our{' '}
                  <Link to="/provider/terms" className="provider-link">
                    Terms of Service
                  </Link>{' '}
                  — it covers an on-site sales associate visit to recommission your device.
                </li>
              </ul>
              <div className="provider-payment-actions">
                <button
                  type="button"
                  className="provider-btn provider-btn--primary provider-btn--sm provider-btn--inline"
                  onClick={resetFunnel}
                >
                  Keep my service
                </button>
                <button
                  type="button"
                  className="provider-btn provider-btn--secondary provider-btn--sm provider-btn--inline"
                  onClick={() => setStage(4)}
                >
                  Continue to stop
                </button>
              </div>
            </div>
          )}

          {/* ---------- Stage 4: final confirmation ---------- */}
          {status === 'active' && stage === 4 && (
            <div className="provider-funnel">
              {renderStepIndicator()}
              <h3 className="provider-funnel-title">Confirm service stop</h3>
              <div className="provider-confirm-box">
                <p>
                  This deactivates your device now. A confirmation email goes to{' '}
                  <strong>support@retechsys.ai</strong> with a copy to your email on file.
                </p>
                <label className="provider-funnel-ack">
                  <input
                    type="checkbox"
                    checked={feeAcknowledged}
                    onChange={(e) => setFeeAcknowledged(e.target.checked)}
                  />
                  <span>
                    I understand a <strong>$50 reactivation fee</strong> applies when I resume
                    service, per the Terms of Service.
                  </span>
                </label>
                <div className="provider-payment-actions" style={{ marginTop: 14 }}>
                  <button
                    type="button"
                    className="provider-btn provider-btn--secondary provider-btn--sm provider-btn--inline"
                    onClick={resetFunnel}
                    disabled={sending}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="provider-btn provider-btn--primary provider-btn--sm provider-btn--inline"
                    onClick={() => handleServiceRequest('stop')}
                    disabled={sending || !feeAcknowledged}
                  >
                    {sending ? 'Stopping…' : 'Stop service'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ---------- Stopped ---------- */}
          {status === 'stopped' && (
            <>
              <p className="provider-service-note">
                Your service is stopped and the device is deactivated — it is not generating
                fortunes. You can resume anytime; per our{' '}
                <Link to="/provider/terms" className="provider-link">
                  Terms of Service
                </Link>
                , a <strong>$50 reactivation fee</strong> applies, covering an on-site sales
                associate visit to recommission your device. Our team will follow up to arrange
                it.
              </p>
              <button
                type="button"
                className="provider-btn provider-btn--primary provider-btn--sm provider-btn--inline"
                onClick={() => handleServiceRequest('resume')}
                disabled={sending}
              >
                {sending ? 'Resuming…' : 'Resume service'}
              </button>
            </>
          )}
        </>
      )}
    </div>
  );
}

export default ServiceControl;
